import { MapPin, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import { TransportSaveFailedBanner } from '~/components/ui/TransportSaveFailedBanner';
import type { TransportSaveFailure } from '~/types/transportSave';
import type {
  Cart as CartType,
  Facility,
  LogisticsIncoterm,
  Location,
  VehicleType,
} from '~/graphql/graphql';
import { formatOrderTotal } from '~/utils';
import {
  getContainerVehicles,
  type VehicleOption,
} from '~/utils/vehicleGroups';
import { formatAddressText, hasCoordinates } from '~/utils/addressFormat';
import {
  TransportCard,
  type DomesticDeliveryMethod,
} from '~/components/cards/TransportCard.tsx';
import { InternationalShippingSection } from './InternationalShippingSection';
import { DeliveryAddressSection } from './DeliveryAddressSection';

interface InternationalShippingInfo {
  incoTerm: LogisticsIncoterm | null | undefined;
  incotermDischarge: CartType['incotermDischarge'];
  incotermAddressDestination: CartType['incotermAddressDestination'];
  deliveryMethod: CartType['deliveryMethod'];
  portOfLoading: CartType['portOfLoading'];
  portOfDischarge: CartType['portOfDischarge'];
  shippingRoute: CartType['shippingRoute'];
}

interface OrderSummaryProps {
  shippingLocation: Location;
  deliveryAddress?: CartType['deliveryAddress'];
  subtotal: number | null;
  currency?: string;
  itemCount: number;
  onCheckout: () => void;
  isPlaceOrderDisabled: boolean;
  /** True while pending quantity changes are being saved before navigating to checkout. */
  isCheckingOut?: boolean;
  sellerOrganizationFacility: Facility;
  shippingLocationFacility?: Facility;
  deliveryMethod: DomesticDeliveryMethod;
  selectedTransportVehicleId: string;
  supportedVehicleIds?: string[];
  cartId: string;
  isInternational?: boolean;
  isSubOrder?: boolean;
  international?: InternationalShippingInfo;
  /**
   * Whether the selected vehicle is usable for this cart. For international
   * carts, a non-INTERNATIONAL_CONTAINER vehicle is "not usable" — distinct
   * from "no vehicle id". Falls back to `!!selectedTransportVehicleId` for
   * backward compatibility with callers that don't compute this.
   */
  hasUsableTransportVehicle?: boolean;
  transportSaveFailure: TransportSaveFailure;
  onRetryTransportSave: () => void;
  productsNeedingCapacity: string[];
  isTransportSaving: boolean;
  hasUnresolvedBundleSize?: boolean;
  transportVehicleCatalog?: VehicleOption[];
  onTransportChange: (
    deliveryMethod: DomesticDeliveryMethod,
    transportVehicleId: string,
    vehicleType?: VehicleType
  ) => void;
  tarp: boolean;
  onTarpChange: (checked: boolean) => void;
}

export function OrderSummary({
  shippingLocation,
  deliveryAddress = null,
  subtotal,
  currency,
  itemCount,
  onCheckout,
  isPlaceOrderDisabled,
  isCheckingOut = false,
  sellerOrganizationFacility,
  shippingLocationFacility,
  deliveryMethod,
  selectedTransportVehicleId,
  supportedVehicleIds,
  onTransportChange,
  cartId,
  isInternational,
  isSubOrder = false,
  international,
  hasUsableTransportVehicle = !!selectedTransportVehicleId,
  transportSaveFailure,
  onRetryTransportSave,
  productsNeedingCapacity,
  isTransportSaving,
  hasUnresolvedBundleSize = false,
  transportVehicleCatalog,
  tarp,
  onTarpChange,
}: Readonly<OrderSummaryProps>) {
  const { t } = useTranslation('cart');

  const containerVehicles =
    isInternational && international
      ? getContainerVehicles(transportVehicleCatalog, supportedVehicleIds)
      : [];

  const total = subtotal;

  const checkoutBlockedReason = ((): string | null => {
    if (hasUnresolvedBundleSize) return t('cart.bundle_size_required');
    if (transportSaveFailure.kind !== 'none') return null;
    if (productsNeedingCapacity.length > 0)
      return t('cart.vehicle_capacity_required');
    return t('cart.international.required_to_checkout');
  })();

  return (
    <div className="lg:col-span-1 border-t lg:border-t-0 lg:border-l border-neut-100 bg-neut-50/50 p-4 sm:p-6 flex flex-col gap-6">
      {/* Address Display */}
      <div>
        <label className="flex items-center gap-2 text-sm text-neut-700 mb-3 font-medium">
          <MapPin className="w-4 h-4 text-prim-500" />
          {t('cart.shipping_address')}
        </label>
        <div>
          <div className="p-3 bg-neut-0 rounded-lg border border-neut-200">
            <p
              className="text-sm font-medium text-neut-prim mb-1"
              data-testid="shipping-address-name"
            >
              {shippingLocation.name}
            </p>
            <p className="text-sm text-neut-700">
              {shippingLocation.facility.address.addressLine1}
            </p>
            {(shippingLocation.facility.address.city ||
              shippingLocation.facility.address.region ||
              shippingLocation.facility.address.zipCode) && (
              <p className="text-sm text-neut-700 mt-1">
                {[
                  shippingLocation.facility.address.city,
                  shippingLocation.facility.address.region,
                  shippingLocation.facility.address.zipCode,
                ]
                  .filter(Boolean)
                  .join(' ')}
              </p>
            )}
          </div>
          <p className="text-xs text-neut-sec mt-2 italic">
            {t('cart.shipping_address_readonly_hint')}
          </p>
        </div>
      </div>

      {!isSubOrder && (
        <DeliveryAddressSection
          cartId={cartId}
          deliveryAddress={deliveryAddress}
          shippingLocationName={shippingLocation.name}
          fallbackCenter={
            hasCoordinates(shippingLocation.facility.address)
              ? {
                  latitude: shippingLocation.facility.address.latitude,
                  longitude: shippingLocation.facility.address.longitude,
                }
              : null
          }
        />
      )}

      {isInternational && international ? (
        <InternationalShippingSection
          cartId={cartId}
          deliveryMethod={international.deliveryMethod}
          incoTerm={international.incoTerm}
          incotermDischarge={international.incotermDischarge}
          incotermAddressDestination={international.incotermAddressDestination}
          portOfLoading={international.portOfLoading}
          portOfDischarge={international.portOfDischarge}
          shippingRoute={international.shippingRoute}
          sellerNearestPort={sellerOrganizationFacility.nearestPort ?? null}
          buyerNearestPort={shippingLocationFacility?.nearestPort ?? null}
          deliveryAddressText={
            deliveryAddress ? formatAddressText(deliveryAddress) : null
          }
          buyerFacilityAddress={
            deliveryAddress
              ? formatAddressText(deliveryAddress)
              : shippingLocationFacility
                ? formatAddressText(shippingLocationFacility.address)
                : null
          }
          isSubOrder={isSubOrder}
          containerVehicles={containerVehicles}
          selectedTransportVehicleId={selectedTransportVehicleId}
          onTransportChange={onTransportChange}
        />
      ) : (
        <TransportCard
          sellerOrganizationFacility={sellerOrganizationFacility}
          shippingLocationFacility={shippingLocationFacility}
          deliveryMethod={deliveryMethod}
          selectedTransportVehicleId={selectedTransportVehicleId}
          supportedVehicleIds={supportedVehicleIds}
          transportVehicleCatalog={transportVehicleCatalog}
          onTransportChange={onTransportChange}
        />
      )}
      <TransportSaveFailedBanner
        failure={transportSaveFailure}
        onRetry={onRetryTransportSave}
        productsNeedingCapacity={productsNeedingCapacity}
        isSaving={isTransportSaving}
      />

      <div className="rounded-lg border border-neut-200 bg-neut-0 p-4">
        <label className="flex cursor-pointer select-none items-center gap-3">
          <input
            type="checkbox"
            checked={tarp}
            onChange={(e) => onTarpChange(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-neut-prim">{t('cart.tarp_label')}</span>
        </label>
      </div>

      {/* Order Summary */}
      <div className="flex-1 flex flex-col">
        <h4 className="text-sm text-neut-prim font-semibold mb-3">
          {t('cart.order_summary')}
        </h4>

        <div className="space-y-2.5 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-neut-sec font-normal">
              {t('cart.subtotal', { itemsCount: itemCount })}
            </span>
            <span
              data-testid="cart-subtotal"
              className="text-neut-prim font-medium"
            >
              {formatOrderTotal(subtotal, t('cart.not_available'), currency)}
            </span>
          </div>

          <div className="border-t border-neut-300 pt-2.5 flex items-center justify-between">
            <span className="text-neut-prim font-semibold">
              {t('cart.total')}
            </span>
            <span
              data-testid="cart-total"
              className="text-xl text-neut-prim font-semibold"
            >
              {formatOrderTotal(total, t('cart.not_available'), currency)}
            </span>
          </div>
        </div>

        {/* Place Order Button */}
        <div className="mt-auto">
          <Button
            variant="primary"
            onClick={onCheckout}
            disabled={isPlaceOrderDisabled || isCheckingOut}
          >
            <span className="flex items-center justify-center gap-2">
              <CreditCard className="w-4 h-4" />
              {isCheckingOut ? t('cart.checkout_pending') : t('cart.checkout')}
            </span>
          </Button>

          {!hasUsableTransportVehicle && (
            <p className="text-xs text-warning-600 text-center mt-2">
              {isInternational
                ? t('cart.container_required')
                : t('cart.transport_required')}
            </p>
          )}
          {hasUsableTransportVehicle &&
            isPlaceOrderDisabled &&
            checkoutBlockedReason && (
              <p className="text-xs text-warning-600 text-center mt-2">
                {checkoutBlockedReason}
              </p>
            )}
        </div>
      </div>
    </div>
  );
}
